/* Replace assets/product-recommendations.js with this entire file. */
if (!customElements.get('product-recommendations')) {
  const anchorHolds = new Map();
  const nextFrame = () => new Promise(resolve => requestAnimationFrame(resolve));

  // Pause automatic scroll corrections, not wheel/touch/keyboard scrolling.
  function holdScrollAnchoring(element) {
    const roots = new Set([document.documentElement, document.body]);
    for (let parent = element.parentElement; parent; parent = parent.parentElement) {
      if (/auto|scroll/.test(getComputedStyle(parent).overflowY)) roots.add(parent);
    }
    roots.forEach(root => {
      let hold = anchorHolds.get(root);
      if (!hold) {
        hold = { count: 0, value: root.style.getPropertyValue('overflow-anchor'),
          priority: root.style.getPropertyPriority('overflow-anchor') };
        anchorHolds.set(root, hold);
        root.style.setProperty('overflow-anchor', 'none', 'important');
      }
      hold.count++;
    });
    let released = false;
    return () => {
      if (released) return;
      released = true;
      roots.forEach(root => {
        const hold = anchorHolds.get(root);
        if (--hold.count) return;
        if (hold.value) root.style.setProperty('overflow-anchor', hold.value, hold.priority);
        else root.style.removeProperty('overflow-anchor');
        anchorHolds.delete(root);
      });
    };
  }

  function imageReady(image, signal) {
    if (image.complete || signal.aborted) return Promise.resolve();
    return new Promise(resolve => {
      const finish = () => {
        clearTimeout(timer);
        image.removeEventListener('load', finish);
        image.removeEventListener('error', finish);
        signal.removeEventListener('abort', finish);
        resolve();
      };
      // A slow/broken thumbnail must not hide the product name and add button.
      const timer = setTimeout(finish, 1400);
      image.addEventListener('load', finish, { once: true });
      image.addEventListener('error', finish, { once: true });
      signal.addEventListener('abort', finish, { once: true });
      if (image.complete || signal.aborted) finish();
    });
  }

  customElements.define('product-recommendations', class extends HTMLElement {
    connectedCallback() {
      if (this.loading || this.loaded || this.scheduled) return;
      if (this.querySelector('.product-card')) {
        this.loaded = true;
        this.setAttribute('aria-busy', 'false');
        return;
      }
      if (this.classList.contains('complementary-products')) {
        this.init();
        return;
      }
      // Keep lower-page related products on Sleek's existing schedule.
      this.scheduled = true;
      const load = () => {
        this.scheduled = false;
        if (this.isConnected) this.init();
      };
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(load, { timeout: 1500 });
      } else {
        FoxTheme.Motion.inView(this, load, { margin: '0px 0px 400px 0px' });
      }
    }

    disconnectedCallback() {
      this.fetchController?.abort();
      this.revealController?.abort();
      this.animations?.forEach(animation => animation.cancel());
      this.releaseAnchoring?.();
    }

    get reducedMotion() {
      return matchMedia('(prefers-reduced-motion: reduce)').matches ||
        Boolean(window.FoxTheme?.config?.disableAnimations || window.FoxTheme?.config?.motionReduced);
    }

    async tween(element, properties, duration) {
      if (this.revealController?.signal.aborted) return;
      const applyEnd = () => Object.entries(properties).forEach(([key, values]) => {
        element.style[key] = values[values.length - 1];
      });
      if (this.reducedMotion || !element.animate) {
        applyEnd();
        return;
      }
      const animation = element.animate(properties, {
        duration, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'both'
      });
      this.animations ||= new Set();
      this.animations.add(animation);
      try { await animation.finished; } catch (_) { /* Cancelled on removal/resize. */ }
      if (!this.revealController?.signal.aborted) applyEnd();
      animation.cancel();
      this.animations.delete(animation);
    }

    async revealPairs(recommendations) {
      this.revealController = new AbortController();
      const signal = this.revealController.signal;
      const saved = { height: this.style.height, overflow: this.style.overflow };
      const release = holdScrollAnchoring(this);
      this.releaseAnchoring = release;
      let swiper, allowTouchMove, controls, controlsInert;
      const finishEarly = () => {
        this.revealController.abort();
        this.animations?.forEach(animation => animation.cancel());
      };
      const onSummary = event => {
        if (event.target instanceof Element && event.target.closest('summary')) finishEarly();
      };
      window.addEventListener('resize', finishEarly, { once: true });
      this.addEventListener('pointerdown', onSummary);
      this.addEventListener('keydown', onSummary);

      try {
        // All rows can lay out inside this clipped box without moving the page.
        this.style.height = `${this.getBoundingClientRect().height}px`;
        this.style.overflow = 'hidden';
        recommendations.querySelectorAll('.product-card').forEach(card => {
          card.setAttribute('data-fb-pair-enter', '');
          card.style.opacity = '0';
          card.inert = true;
        });
        this.innerHTML = recommendations.innerHTML;
        await nextFrame();
        if (!this.isConnected || signal.aborted) return;

        swiper = this.querySelector('complementary-product-slider')?.sliderInstance?.slider;
        if (swiper) {
          allowTouchMove = swiper.allowTouchMove;
          swiper.allowTouchMove = false;
          swiper.update();
          swiper.updateAutoHeight(0);
        }
        controls = this.querySelector('.slider-controls--group');
        if (controls) { controlsInert = controls.inert; controls.inert = true; }
        const slide = this.querySelector('.swiper-slide-active') ||
          this.querySelector('.swiper-slide:not(.swiper-slide-duplicate)') || this;
        const cards = Array.from(slide.querySelectorAll('.product-card'));
        // Start every thumbnail wait together, then reveal in the editor's order.
        const ready = cards.map(card => Promise.all(
          Array.from(card.querySelectorAll('img')).map(image => imageReady(image, signal))
        ));
        for (let index = 0; index < cards.length; index++) {
          await ready[index];
          if (!this.isConnected || signal.aborted) break;
          const card = cards[index];
          swiper?.updateAutoHeight(0);
          const aside = this.querySelector('aside');
          const target = index === cards.length - 1 && aside
            ? aside.getBoundingClientRect().height
            : card.getBoundingClientRect().bottom - this.getBoundingClientRect().top;
          card.inert = false;
          await Promise.all([
            this.tween(this, { height: [`${this.getBoundingClientRect().height}px`, `${target}px`] }, index ? 460 : 280),
            this.tween(card, { opacity: ['0', '1'] }, 320)
          ]);
        }
      } finally {
        this.revealController.abort();
        window.removeEventListener('resize', finishEarly);
        this.removeEventListener('pointerdown', onSummary);
        this.removeEventListener('keydown', onSummary);
        // Clear both original slides and any copies created by Swiper's loop.
        this.querySelectorAll('[data-fb-pair-enter]').forEach(card => {
          card.style.removeProperty('opacity');
          card.inert = false;
          card.removeAttribute('data-fb-pair-enter');
        });
        if (swiper && !swiper.destroyed) {
          swiper.allowTouchMove = allowTouchMove;
          swiper.updateAutoHeight(0);
        }
        if (controls) controls.inert = controlsInert;
        this.style.height = saved.height;
        this.style.overflow = saved.overflow;
        // Let the final layout settle before restoring the browser's anchoring.
        requestAnimationFrame(() => requestAnimationFrame(release));
      }
    }

    async dismissPairs() {
      const wrapper = this.closest('.product__block');
      if (!wrapper) return;
      this.revealController = new AbortController();
      const release = holdScrollAnchoring(this);
      this.releaseAnchoring = release;
      const style = getComputedStyle(wrapper);
      wrapper.style.overflow = 'hidden';
      try {
        await this.tween(wrapper, {
          height: [`${wrapper.getBoundingClientRect().height}px`, '0px'],
          marginTop: [style.marginTop, '0px'],
          marginBottom: [style.marginBottom, '0px'],
          opacity: ['1', '0']
        }, 320);
        // Keep the hold through our own removal; disconnectedCallback handles
        // unrelated removals while a request or animation is still running.
        this.releaseAnchoring = null;
        wrapper.remove();
      } finally {
        requestAnimationFrame(() => requestAnimationFrame(release));
      }
    }

    async init() {
      if (this.loading || this.loaded || !this.isConnected || !this.dataset.url) return;
      this.loading = true;
      const complementary = this.classList.contains('complementary-products');
      const softReveal = complementary && this.hasAttribute('data-fb-pairs');
      this.fetchController = new AbortController();
      const timeout = setTimeout(() => this.fetchController.abort(), 10000);
      try {
        const response = await fetch(this.dataset.url, {
          signal: this.fetchController.signal,
          ...(complementary ? { priority: 'high' } : {})
        });
        if (!response.ok) throw new Error(`Recommendations request failed (${response.status})`);
        const responseText = await response.text();
        clearTimeout(timeout);
        if (!this.isConnected) return;
        const section = new DOMParser().parseFromString(responseText, 'text/html').querySelector('.shopify-section');
        const recommendations = section?.querySelector(complementary
          ? 'product-recommendations.complementary-products'
          : 'product-recommendations:not(.complementary-products)');
        if (!recommendations) throw new Error('Recommendations markup was missing');

        if (!recommendations.querySelector('.product-card')) {
          this.loaded = true;
          this.dispatchEvent(new CustomEvent('recommendations:empty'));
          if (softReveal) await this.dismissPairs();
          else this.closest(complementary ? '.product__block' : '.shopify-section')?.remove();
          return;
        }
        if (complementary) {
          recommendations.querySelectorAll('.product-card img').forEach(image => {
            image.setAttribute('loading', 'eager');
            image.setAttribute('fetchpriority', 'auto');
          });
        }
        if (softReveal) await this.revealPairs(recommendations);
        else this.innerHTML = recommendations.innerHTML;
        this.loaded = true;
        if (this.isConnected) this.dispatchEvent(new CustomEvent('recommendations:loaded'));
      } catch (error) {
        if (this.isConnected) {
          console.error('[Product recommendations]', error);
          this.dispatchEvent(new CustomEvent('recommendations:error'));
          if (softReveal && this.querySelector('.fb-pairs-placeholder')) await this.dismissPairs();
        }
      } finally {
        clearTimeout(timeout);
        this.loading = false;
        this.setAttribute('aria-busy', 'false');
      }
    }
  });
}
